/* eslint-disable react/prop-types */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useSortBy, useTable } from "react-table";
import { toast } from "react-toastify";
import TableLayout from "../../../../components/quotationsTable/table-layout";
import { Cell } from "../../../../components/quotationsTable/table-instance";
import { QUOTATIONS_COLUMN } from "../../../../components/quotationsTable/Constants";
import { request } from "../../../../config/helpers/axios-instance";
import { QUOTATION_STATUS_REGISTRY } from "../../../constants/quotation-status";
import { buildExtraPharmacyQuotationColumns } from "./columns";

function StatusSelect({ row, refetchOrders }) {
    const currentStatus = row?.row?.original?.status;
    const [value, setValue] = useState(currentStatus);
    const [saving, setSaving] = useState(false);

    useEffect(() => setValue(currentStatus), [currentStatus]);

    const handleChange = async (event) => {
        const next = event.target.value;
        setValue(next);
        setSaving(true);
        try {
            const response = await request({
                method: "PUT",
                url: `/quotation/admin/id/${row.row.original.id}`,
                data: { status: next },
            });
            if (!response.data.success) {
                toast.warn("Please Try After Sometime");
                setValue(currentStatus);
            } else {
                refetchOrders();
            }
        } catch (error) {
            toast.warn("Please Try After Sometime");
            setValue(currentStatus);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="px-2 text-black">
            <select
                style={{ border: "1px solid #b0b5ba", borderRadius: "6px" }}
                className="w-[100%]"
                value={value}
                disabled={saving}
                onClick={(event) => event.stopPropagation()}
                onChange={handleChange}
            >
                {Object.entries(QUOTATION_STATUS_REGISTRY).map(([code, meta]) => (
                    <option key={code} value={code}>
                        {meta.label}
                    </option>
                ))}
            </select>
        </div>
    );
}

/**
 * Shared by /v2/pharmacy/quotations and /v2/sales/quotations.
 *
 * `extraColumns`, `omitHeaders` and `quotationType` all default to the pharmacy set, so
 * that route renders exactly what it did before. A prop rather than a forked copy: the
 * registry-driven StatusSelect above is the whole reason this component exists, and a
 * second copy of it would be a second place for the STATUS_MAP bug to come back.
 *
 * `omitHeaders` drops columns from legacy's QUOTATIONS_COLUMN by Header string. The
 * Sales console uses it for Duration / Left / Inventory No, which the Sales prototype
 * deliberately removes from its Quotations view.
 */
// Frozen module-level default. MUST NOT be an inline `= []`: that allocates a new array
// on every render, which changes the useMemo dependency below every render, rebuilds
// `columns` every render, and sends react-table into an infinite update loop
// ("Maximum update depth exceeded"). The Sales console passes its own module-level
// constant for the same reason.
const NO_OMITTED_HEADERS = Object.freeze([]);

function PharmacyQuotationsTableInstance({
    tableData,
    pageSort,
    setPageSort,
    handleDeleteRow,
    refetchOrders,
    extraColumns = buildExtraPharmacyQuotationColumns,
    omitHeaders = NO_OMITTED_HEADERS,
    quotationType = "PHARMACY_QUOTATIONS",
}) {
    const columns = React.useMemo(
        () => [
            ...QUOTATIONS_COLUMN.filter((column) => !omitHeaders.includes(column.Header)),
            ...extraColumns(),
            {
                Header: "Status",
                accessor: "status",
                Cell: (row) => <StatusSelect row={row} refetchOrders={refetchOrders} />,
            },
            {
                Header: "Action",
                accessor: "action",
                Cell: (row) => Cell(row, () => {}, handleDeleteRow, quotationType),
            },
        ],
        [refetchOrders, handleDeleteRow, extraColumns, omitHeaders, quotationType]
    );

    const tableInstance = useTable(
        {
            columns,
            data: tableData,
            initialState: { sortBy: pageSort },
            manualSortBy: true,
            autoResetSortBy: false,
            autoResetExpanded: false,
            autoResetPage: false,
        },
        useSortBy
    );

    return <TableLayout {...tableInstance} setPageSort={setPageSort} />;
}

PharmacyQuotationsTableInstance.propTypes = {
    tableData: PropTypes.array.isRequired,
    pageSort: PropTypes.any,
    setPageSort: PropTypes.func,
    handleDeleteRow: PropTypes.func,
    refetchOrders: PropTypes.func,
    // Optional -- all three default to the pharmacy console's own configuration.
    extraColumns: PropTypes.func,
    omitHeaders: PropTypes.arrayOf(PropTypes.string),
    quotationType: PropTypes.string,
};

export default PharmacyQuotationsTableInstance;
